import { APIResponse, LoginSource } from '@arema/components/Classes';
import { PAGE_AUTH } from './ReportsConfig';
import { EventList, ReportType } from './ReportsClasses';
import axios, { ResponseType } from 'axios';
import moment from 'moment';
const ROOT_URL = process.env.REACT_APP_API_URL
const DEBUG = process.env.REACT_APP_DEBUG==='1';

export interface TokenData {
	access: string,
	expires: number,
	login_type: number,
}

var CACHE : { [x: string]: APIResponse<any> } = {};

var getToken = (): TokenData => {
	var jsonstr = localStorage.getItem('user_external_token');
	if(!jsonstr) return null;
	try{
		var data = JSON.parse(jsonstr) as TokenData;
		if(!data) return null;
		if(!data.access) return null;
		var now = moment().unix();
		if(data.expires<=(now+30)) return null;
		return data;
	}catch(e){
		return null;
	}
}

var saveToken = (token: TokenData) => {
	localStorage.setItem('user_external_token', JSON.stringify(token));
	window.dispatchEvent(new Event('user_external_token'));
}

var logout = () => {
	localStorage.removeItem('user_external_token');
	localStorage.removeItem('user_external_data');
	window.dispatchEvent(new Event('user_external_data'));
	window.dispatchEvent(new Event('user_external_logout'));
}

interface PostOptions {
	login?: boolean,
	response_type?: ResponseType,
	access_token?: string,
	login_type?: number,
}

async function post<T = any>(endpoint: string, data?: any, opts: PostOptions = {}): Promise<APIResponse<T>> {
	var options = {
		headers: {
			'show-code': DEBUG ? 1 : 0,
			'token': '',
			'Content-Type': 'application/json'
		}
	}
	if(opts?.login !== false){
		if(opts?.access_token){
			options.headers.token = `${opts.login_type}:::${opts.access_token}`;
		} else {
			var u = getToken();
			if(u === null){
				logout();
				return { error: true, code: 'PV-INVCRD', message: 'Las credenciales de accceso no son válidas (LCL-TKNCH-L).' }
			}
			options.headers.token = `${u.login_type}:::${u.access}`;
		}
	}
	if(data instanceof FormData){
		options.headers['Content-Type'] = 'multipart/form-data';
	}

	try{
		var ENDPOINT = `${ROOT_URL}/${endpoint}`;
		if(DEBUG) console.log("POST " + ENDPOINT);
		var res = await axios.post(ENDPOINT, data, {
			...options,
			responseType: opts?.response_type || 'json'
		});
		if(DEBUG) console.log(` => Response: ${(res.headers['content-length'] / 1024).toFixed(2)} KB`);
		if(res.data && res.data.error && res.data.code == 'P-TKNE'){
			logout();
		}
		return res.data as APIResponse<T>;
	}catch(err){
		if(DEBUG) console.error(err);
		if((err as any)?.code === "ERR_NETWORK"){
			return {
				error: true,
				message: 'No se pudo establecer una conexión con el servidor. (API-NONTWK)',
				code: 'API-NONTWK',
			}
		}
		return {
			error: true,
			message: 'Hubo un error inesperado (API-LCL-1)',
			code: 'API-LCL-1',
		}
	}
};

var cachePost = async <T=any>(endpoint: string, key: string, data?: any) : Promise<APIResponse<T>>=>{
	if(CACHE[key]) return CACHE[key] as APIResponse<T>;
	var res = await post(endpoint, data);
	if(!res.error) CACHE[key] = res;
	return res;
}

var getAuthUrl = (redirect?: string) => {
	return `${PAGE_AUTH}/external?s=${LoginSource.REPORTS}&continue=${encodeURIComponent(`${window.location.origin}/auth`)}&redirect=${encodeURIComponent((redirect || window.location.href))}&ac=1`
}

export interface EventAccess {
	event_id: number,
	access: number[]
}

export interface UserLogin {
	user: {
		admin_id?: number,
		external_id?: number,
		all_access?: boolean,
		first_name: string,
		last_name: string,
		email: string,
		change_password: boolean,
	},
	access: EventAccess[],
}

var USER_LOGIN_CACHE: UserLogin = null;

var login = async (username: string, password: string, type: number) => {
	var res = await post('stats/login', { username, password, type }, { login: false })
	if(!res.error && res.data.tokens){
		USER_LOGIN_CACHE = null;
		localStorage.setItem('user_external_token', JSON.stringify(res.data.tokens));
	}
	return res;
}

var getLogin = async (force: boolean = false, token: string = null, login_type: number = 1, dispatch_event: boolean = true) => {
	if(!force && USER_LOGIN_CACHE) return { error: false, data: USER_LOGIN_CACHE, message: '' };
	var res = await post<UserLogin>('stats/me', {}, {
		access_token: token,
		login_type
	});
	if(res.error) return res;
	USER_LOGIN_CACHE = res.data;
	localStorage.setItem('user_external_data', JSON.stringify(res.data));
	if(dispatch_event) window.dispatchEvent(new Event('user_external_data'));
	return res;
}

var onLoginToken = async (login_type: number, token: string, expiration: number, dispatch_event: boolean = true) => {
	var res = await getLogin(true, token, login_type, false);
	if(!res.error){
		saveToken({ access: token, expires: expiration, login_type });
		if(dispatch_event) window.dispatchEvent(new Event('user_external_data'));
	}
	return res;
}

var getEvents = (offset: number = 0, count: number = 50) => {
	return post<EventList[]>('stats/events/list', { offset, count });
}

var getEventInfo = (event_id: number)=>{
	return cachePost<{ event_id: number, event_name: string }>('stats/events/info', `EVINFO-${event_id}`, { event_id });
}

var getReportDownload = async (event_id: number, date_id: number, type: ReportType, filename: string)=>{
	var res = (await post(`stats/report/${type}`, { event_id, date_id }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type === 'application/json'){
		return JSON.parse(await res.text()) as APIResponse;
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = filename;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	};
}

var searchEvents = (query: string)=>{
	return post('stats/events/search', { query });
}

var getSeatmap = (date_id: number, section_id: number) => {
	return post('stats/dates/seatmap', { date_id, section_id });
}

var getEventSummary = (event_id: number)=>{
	return cachePost('stats/events/summary', `EVSMRY-${event_id}`, { event_id });
}

var getEventDates = (event_id: number, count: number=20)=>{
	return cachePost('stats/dates/list', `EVDTS-${event_id}`, { event_id, count });
}

var getInnactiveDates = (event_id: number, offset: number, count: number)=>{
	return post('stats/dates/list/innactive', { event_id, offset, count });
}

var getDateInfo = (date_id: number)=>{
	return cachePost('stats/dates/info', `EVDTINF-${date_id}`, { date_id });
}

var getDateSummary = (date_id: number)=>{
	return cachePost('stats/dates/summary', `EVDTSMRY-${date_id}`, { date_id });
}

var getDateSales = (date_id: number)=>{
	return cachePost('stats/dates/sales', `EVDTSLS-${date_id}`, { date_id });
}

var getDateUses = (date_id: number)=>{
	return cachePost('stats/dates/uses', `EVDTUSS-${date_id}`, { date_id });
}

var getEventPayouts = (event_id: number)=>{
	return cachePost('stats/events/payouts', `EVPTTS-${event_id}`, { event_id });
}

var getPayout = (event_id: number, payout_id: number)=>{
	return post('stats/events/payouts/get', { event_id, payout_id });
}

export default {
	// downloadReportCsv,
	getAuthUrl,
	getDateInfo,
	getDateSales,
	getDateSummary,
	getDateUses,
	getEventDates,
	getEventInfo,
	getEventPayouts,
	getEvents,
	getEventSummary,
	getInnactiveDates,
	getLogin,
	getPayout,
	getReportDownload,
	getSeatmap,
	getToken,
	login,
	logout,
	onLoginToken,
	searchEvents
}